/**
 * User roles for role-based access control
 * Maps to Req 76 AC 2: role-based views
 * Aligned with backend users.json roles
 */
export enum UserRole {
  ADMIN = 'admin',
  ANALYST = 'analyst',
  VIEWER = 'viewer',
}

/**
 * Role permissions matrix
 * Defines what each role can do in the platform
 */
export const ROLE_PERMISSIONS = {
  [UserRole.ADMIN]: {
    viewDashboard: true,
    viewCostExplorer: true,
    viewTagging: true,
    viewAnomalies: true,
    acknowledgeAnomalies: true,
    editCostAllocationTags: true,
    adminConsole: true,
    manageUsers: true,
  },
  [UserRole.ANALYST]: {
    viewDashboard: true,
    viewCostExplorer: true,
    viewTagging: true,
    viewAnomalies: true,
    acknowledgeAnomalies: true,
    editCostAllocationTags: false,
    adminConsole: false,
    manageUsers: false,
  },
  [UserRole.VIEWER]: {
    viewDashboard: true,
    viewCostExplorer: true,
    viewTagging: true,
    viewAnomalies: true,
    acknowledgeAnomalies: false,
    editCostAllocationTags: false,
    adminConsole: false,
    manageUsers: false,
  },
} as const;

export type Permission = keyof typeof ROLE_PERMISSIONS[UserRole.ADMIN];

/**
 * User authentication state
 */
export interface User {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  tenantId: string;
  preferences: UserPreferences;
}

/**
 * User preferences including dark mode
 * Maps to Req 166 AC 3: dark mode support
 */
export interface UserPreferences {
  darkMode: boolean;
  language: string;
  timezone: string;
}

/**
 * Authentication state
 */
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Auth token response
 */
export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}
